import { useState, FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { Eye, EyeOff } from "lucide-react"

export function LoginPage() {
  const navigate = useNavigate()
  const [email, setEmail]       = useState("")
  const [password, setPassword] = useState("")
  const [showPw, setShowPw]     = useState(false)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      })
      if (authError) { setError("Invalid credentials. Please try again."); return }
      navigate("/", { replace: true })
    } catch {
      setError("An unexpected error occurred.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zimrah-950 px-4">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-zimrah-700/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-sm relative">
        {/* Logo + branding */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center justify-center w-20 h-20 rounded-3xl bg-white/10 backdrop-blur-sm shadow-2xl shadow-zimrah-900/80 mb-5 p-4 border border-white/10">
            <img src="/logo.svg" alt="Zimrah" className="w-full h-full" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">Zimrah Health</h1>
          <p className="text-zimrah-400 text-sm mt-1 font-medium">Admin Panel</p>
          <div className="mt-2 px-3 py-1 rounded-full bg-red-900/40 border border-red-800/50">
            <p className="text-[11px] font-bold text-red-400 uppercase tracking-wider">Restricted Access</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-zimrah-300 mb-1.5">
                  Email address
                </label>
                <input
                  id="email" type="email" autoComplete="email" required
                  value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-zimrah-950/80 border border-zimrah-800/60 text-white placeholder-zimrah-700 text-sm focus:outline-none focus:ring-2 focus:ring-zimrah-500 focus:border-transparent transition-all"
                  placeholder="admin@zimrah.co"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-zimrah-300 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required
                    value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-11 rounded-xl bg-zimrah-950/80 border border-zimrah-800/60 text-white placeholder-zimrah-700 text-sm focus:outline-none focus:ring-2 focus:ring-zimrah-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zimrah-600 hover:text-zimrah-300 transition-colors p-1"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <div role="alert" className="flex items-start gap-2 text-sm text-red-300 bg-red-950/50 border border-red-900/50 rounded-xl px-4 py-3">
                  <span className="shrink-0 mt-0.5">⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full py-3 rounded-xl bg-zimrah-600 hover:bg-zimrah-500 active:bg-zimrah-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-zimrah-400 focus:ring-offset-2 focus:ring-offset-zimrah-950 shadow-lg shadow-zimrah-900/50 mt-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in…
                  </span>
                ) : "Sign in to Admin"}
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-zimrah-700 text-xs mt-6">
          Need help?{" "}
          <a href="mailto:support@zimrah.co" className="text-zimrah-500 hover:text-zimrah-300 underline transition-colors">
            support@zimrah.co
          </a>
        </p>
      </div>
    </div>
  )
}